/**
 * FotografiasGrid - Grid responsivo de fotos/documentos
 *
 * Epic 2 - Story 2.4: Adicionado indicadores de GPS e suporte a imagens reais
 * Grid 6 colunas (desktop), 4 (tablet), 2 (mobile)
 * Thumbnails de imagem e PDF, botão adicionar, botão excluir
 * Tooltip no hover com nome, classificação, observação e GPS
 */
import { useState, useEffect, useRef } from 'react';
import { FileText, Plus, Trash2, Image as ImageIcon, MapPin } from 'lucide-react';
import { Tooltip } from '@mantine/core';
import type { MockFoto } from '../../hooks/useFotografias';
import { AuthenticatedImage } from '../AuthenticatedImage';

interface FotografiasGridProps {
  fotos: MockFoto[];
  onPhotoClick: (index: number) => void;
  onDelete: (id: number) => void;
  onAdd: () => void;
}

export function FotografiasGrid({
  fotos,
  onPhotoClick,
  onDelete,
  onAdd,
}: FotografiasGridProps) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
          Fotos / Documentos
        </h3>
        <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded-full">
          {fotos.length} {fotos.length === 1 ? 'arquivo' : 'arquivos'}
        </span>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {fotos.map((foto, index) => (
          <FotoCard
            key={foto.id}
            foto={foto}
            onClick={() => onPhotoClick(index)}
            onDelete={() => onDelete(foto.id)}
          />
        ))}

        {/* Botão Adicionar */}
        <AddPhotoCard onClick={onAdd} />
      </div>

      {/* Empty State */}
      {fotos.length === 0 && (
        <div className="text-center py-6 text-slate-500 col-span-full">
          <ImageIcon className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p className="text-xs">Nenhuma foto encontrada</p>
        </div>
      )}
    </div>
  );
}

// ==================== TOOLTIP CONTENT ====================

interface TooltipContentProps {
  foto: MockFoto;
}

function TooltipContent({ foto }: TooltipContentProps) {
  return (
    <div className="space-y-1 text-left max-w-xs">
      {/* Nome do arquivo */}
      <div>
        <span className="text-gray-400 text-[10px]">Arquivo:</span>
        <p className="font-medium text-white text-xs">{foto.fileName}</p>
      </div>

      {/* Classificação (nome completo) */}
      <div>
        <span className="text-gray-400 text-[10px]">Classificação:</span>
        <p className="font-medium text-white text-xs">{foto.classificacao}</p>
      </div>

      {/* Observação/Descrição */}
      {foto.descricao && (
        <div>
          <span className="text-gray-400 text-[10px]">Observação:</span>
          <p className="text-white text-xs">{foto.descricao}</p>
        </div>
      )}

      {/* Cliente */}
      <div>
        <span className="text-gray-400 text-[10px]">Cliente:</span>
        <p className="text-white text-xs">{foto.cliente}</p>
      </div>

      {/* GPS - Epic 2 Story 2.4 */}
      {foto.hasGps && (
        <div className="flex items-center gap-1 pt-1 border-t border-gray-600">
          <MapPin className="w-3 h-3 text-green-400" />
          <span className="text-green-400 text-[10px]">
            {foto.gpsDisplay || 'Localização disponível'}
          </span>
        </div>
      )}
    </div>
  );
}

// ==================== FOTO CARD ====================

interface FotoCardProps {
  foto: MockFoto;
  onClick: () => void;
  onDelete: () => void;
}

function FotoCard({ foto, onClick, onDelete }: FotoCardProps) {
  const isPdf = foto.type === 'pdf';
  const hasRealImage = foto.arquivoUrl && !isPdf;
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  // Detect cached images: if already loaded in browser cache, skip spinner
  useEffect(() => {
    if (!hasRealImage || imageError) return;
    const raf = requestAnimationFrame(() => {
      const img = imgRef.current;
      if (img && img.complete && img.naturalWidth > 0) {
        setImageLoaded(true);
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [hasRealImage, imageError]);

  // Timeout: if image hasn't loaded after 8s, show error state
  useEffect(() => {
    if (!hasRealImage || imageLoaded || imageError) return;
    const timer = setTimeout(() => {
      if (!imageLoaded) setImageError(true);
    }, 8000);
    return () => clearTimeout(timer);
  }, [hasRealImage, imageLoaded, imageError]);

  return (
    <Tooltip
      label={<TooltipContent foto={foto} />}
      position="top"
      withArrow
      multiline
      w={220}
      transitionProps={{ transition: 'fade', duration: 200 }}
    >
      <div className="relative group">
        {/* Card Container */}
        <button
          onClick={onClick}
          className={`w-full aspect-square rounded-lg border-2 overflow-hidden transition-all duration-200 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            isPdf
              ? 'bg-red-50 border-red-100 hover:border-red-300'
              : 'bg-slate-100 border-slate-200 hover:border-blue-400'
          }`}
          aria-label={`Ver ${foto.fileName}`}
        >
          {/* Content */}
          <div className="w-full h-full flex flex-col items-center justify-center p-2 relative">
            {isPdf ? (
              // PDF icon
              <>
                <FileText className="w-8 h-8 text-red-400 mb-1" />
                <span className="text-[10px] font-medium text-slate-700 truncate max-w-full px-1">
                  {foto.fileName}
                </span>
              </>
            ) : hasRealImage && !imageError ? (
              // Real image thumbnail
              <>
                {!imageLoaded && (
                  <ImageIcon className="w-8 h-8 text-slate-300 animate-pulse absolute" />
                )}
                <AuthenticatedImage
                  src={foto.arquivoUrl}
                  alt={foto.fileName}
                  className={`w-full h-full object-cover ${imageLoaded ? '' : 'opacity-0'}`}
                  onLoad={() => setImageLoaded(true)}
                  onError={() => setImageError(true)}
                />
              </>
            ) : (
              // Fallback icon
              <>
                <ImageIcon className="w-8 h-8 text-slate-400 mb-1" />
                <span className="text-[10px] font-medium text-slate-700 truncate max-w-full px-1">
                  {foto.fileName}
                </span>
              </>
            )}

            {/* Classification Badge - always show at bottom for images with URL */}
            {!(hasRealImage && !imageError && imageLoaded) && (
              <span
                className={`mt-1 text-[8px] font-semibold px-1.5 py-0.5 rounded ${
                  isPdf
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-blue-100 text-blue-700'
                }`}
              >
                {foto.classificacaoAbrev}
              </span>
            )}
          </div>
        </button>

        {/* GPS Badge (top left) - Epic 2 Story 2.4 */}
        {foto.hasGps && (
          <div className="absolute top-1 left-1 w-5 h-5 bg-green-500 text-white rounded-full flex items-center justify-center shadow-sm">
            <MapPin className="w-3 h-3" />
          </div>
        )}

        {/* Delete Button (visible on hover) */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-red-600 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-red-400"
          aria-label={`Excluir ${foto.fileName}`}
        >
          <Trash2 className="w-3 h-3" />
        </button>

        {/* Classification Badge for images with real URL (bottom center overlay) */}
        {hasRealImage && !imageError && imageLoaded && (
          <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2">
            <span className="text-[8px] font-semibold px-1.5 py-0.5 rounded bg-blue-600/80 text-white">
              {foto.classificacaoAbrev}
            </span>
          </div>
        )}

        {/* Cliente Badge (bottom left) - só aparece no hover */}
        <div className="absolute bottom-1 left-1 text-[8px] bg-slate-800/70 text-white px-1 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 truncate max-w-[90%]">
          {foto.cliente}
        </div>
      </div>
    </Tooltip>
  );
}

// ==================== ADD PHOTO CARD ====================

interface AddPhotoCardProps {
  onClick: () => void;
}

function AddPhotoCard({ onClick }: AddPhotoCardProps) {
  return (
    <button
      onClick={onClick}
      className="w-full aspect-square rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 flex flex-col items-center justify-center gap-1 transition-all duration-200 hover:border-blue-400 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
      aria-label="Adicionar nova foto"
    >
      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
        <Plus className="w-4 h-4 text-blue-600" />
      </div>
      <span className="text-[10px] font-medium text-slate-600">Adicionar</span>
    </button>
  );
}

export default FotografiasGrid;
